import type { OrderTaskCargoItem, OrderTaskCargoItemProduct } from '@my/tms-api';
import type { AllocationMeasure, CollectionItemOption } from './types';

/**
 * Allocation map: collection cargo item id → amount this delivery draws from
 * it, in that row's measure (a count, kilograms or litres).
 */
export type AllocationMap = Record<string, number>;

/**
 * How a collection row is allocated: by count when it has one (pallet
 * lines), else by weight, else by volume (a tank / bulk load, which a
 * drop-off can take part of). Null when there is nothing to deliver.
 */
export function allocationMeasureFor(item: OrderTaskCargoItem): AllocationMeasure | null {
  if (item.quantity != null && item.quantity > 0) return 'count';
  if (item.weightKg != null && item.weightKg > 0) return 'kg';
  if (item.volumeM3 != null && item.volumeM3 > 0) return 'litres';
  return null;
}

/** The collected amount of a row in its measure. */
export function collectedAmount(item: OrderTaskCargoItem, measure: AllocationMeasure): number {
  switch (measure) {
    case 'count': return item.quantity ?? 0;
    case 'kg': return item.weightKg ?? 0;
    case 'litres': return Math.round((item.volumeM3 ?? 0) * 1000);
  }
}

/**
 * What an existing DELIVERY row takes from its source, in the source's
 * measure — the inverse of `deriveDeliveryItem`.
 */
export function deliveredAmount(item: OrderTaskCargoItem, measure: AllocationMeasure): number {
  switch (measure) {
    case 'count': return item.quantity ?? 0;
    case 'kg': return item.weightKg ?? 0;
    case 'litres': return Math.round((item.volumeM3 ?? 0) * 1000);
  }
}

/** "Sulphuric Acid, Spent · UN 1832", the name + waste code, or the name. */
export function productLineLabel(line: OrderTaskCargoItemProduct): string {
  const suffix = line.productUnNumber ? `UN ${line.productUnNumber}` : line.productEwcCode;
  return suffix ? `${line.productName} · ${suffix}` : line.productName;
}

/** A unit's product(s) as one label: the single product, or all of them in order. */
export function cargoItemProductLabel(item: OrderTaskCargoItem): string | null {
  const lines = (item.products ?? []).slice().sort((a, b) => a.sequence - b.sequence);
  if (lines.length === 0) return null;
  return lines.map(productLineLabel).join(' + ');
}

/**
 * The key a delivery row is allocated under — its product line when it
 * draws from one line of a multi-product unit, else its collection row.
 */
export function allocationKeyOf(item: OrderTaskCargoItem): string | null {
  return item.sourceProductLineId ?? item.sourceCollectionItemId ?? null;
}

/**
 * The physical unit (1-based slot) a DELIVERY row belongs to: the collection
 * unit it draws from; else — a unit-only row, or one saved before anything
 * was allocated — its own `itemIndex`. Clamped into `1..unitCount`, so on a
 * one-unit job every row is unit 1.
 *
 * <p>
 * Delivery rows are numbered in allocation order, not by unit, so reading a
 * delivery's Tank ID / BIC "at itemIndex 1" finds whichever product was
 * allocated first, not unit 1.
 * </p>
 */
export function deliveryUnitSlot(
  item: OrderTaskCargoItem,
  collectionItems: readonly CollectionItemOption[],
  unitCount: number,
): number {
  if (unitCount <= 1) return 1;
  const key = allocationKeyOf(item);
  const unit = (key ? collectionItems.find((c) => c.id === key)?.unitIndex : undefined) ?? item.itemIndex;
  return Math.min(Math.max(unit, 1), unitCount);
}

/** Amount with its unit, for the allocation table: "26", "21,000 kg", "25,000 L". */
export function formatAllocation(amount: number, measure: AllocationMeasure): string {
  const n = amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return measure === 'count' ? n : `${n} ${measure === 'kg' ? 'kg' : 'L'}`;
}

/**
 * Pro-rate a full-row measure (loading metres / weight / volume) down to the
 * delivered portion. e.g. 10 pallets = 4.00 LM, deliver 4 → 1.60 LM.
 * Returns null when the source measure is absent or the pool is empty.
 */
export function proRate(
  full: number | null | undefined,
  deliveredQty: number,
  collectedQty: number,
): number | null {
  if (full == null || collectedQty <= 0) return null;
  return Math.round(((full * deliveredQty) / collectedQty) * 100) / 100;
}

/** Cargo-item fields for a delivery line derived from an allocation. */
export interface DerivedDeliveryItem {
  sourceCollectionItemId: string;
  sourceProductLineId: string | null;
  kind: CollectionItemOption['kind'];
  description: string | null;
  quantityUnit: string | null;
  quantity: number | null;
  loadingMetresM: number | null;
  weightKg: number | null;
  volumeM3: number | null;
}

/**
 * The delivery cargo-item payload for drawing `amount` (in the source's
 * measure) from `coll`. A count lands in Quantity with weight / LM pro-rated,
 * as before. Kilograms land in Weight and litres in Volume, with the other
 * one pro-rated when the collection row has it.
 */
export function deriveDeliveryItem(coll: CollectionItemOption, amount: number): DerivedDeliveryItem {
  const base = {
    sourceCollectionItemId: coll.sourceItemId,
    sourceProductLineId: coll.productLineId,
    kind: coll.kind,
    description: coll.description,
    loadingMetresM: proRate(coll.loadingMetresM, amount, coll.collectedQty),
  };
  switch (coll.measure) {
    case 'count':
      return {
        ...base,
        quantityUnit: coll.quantityUnit,
        quantity: amount,
        weightKg: proRate(coll.weightKg, amount, coll.collectedQty),
        volumeM3: proRate(coll.volumeM3, amount, coll.collectedQty),
      };
    case 'kg':
      return {
        ...base,
        quantityUnit: null,
        quantity: null,
        weightKg: amount,
        volumeM3: proRate(coll.volumeM3, amount, coll.collectedQty),
      };
    case 'litres':
      return {
        ...base,
        quantityUnit: null,
        quantity: null,
        weightKg: proRate(coll.weightKg, amount, coll.collectedQty),
        volumeM3: Math.round((amount / 1000) * 1000) / 1000,
      };
  }
}
